import React, { useEffect, useState } from "react";
import axios from "axios";
import getAPI from "../../service/getAPI";
import { Form } from "react-bootstrap";
import { format } from "date-fns";
import dayjs from "dayjs";

function Dashboard() {
  const { master } = getAPI();
  const [selectedStoreId, setSelectedStoreId] = useState("");
  const [storeList, setStoreList] = useState([]);
  const [dashboardData, setDashboardData] = useState({
    totalOrders: 0,
    totalAmount: 0,
    totalCollected: 0,
    totalDue: 0,
    orderTypeWiseData: [],
    creditData: null,
  });
  const [loggedInUser, setLoggedInUser] = useState(null);
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [dateFilterType, setDateFilterType] = useState("today");
  const [loading, setLoading] = useState(false);

  const fetchDashboardData = (
    selectedStoreId,
    type = "today",
    customStart = null,
    customEnd = null,
  ) => {
    let storeId = selectedStoreId;
    if (storeId === "") return;

    setLoading(true);

    let startDate, endDate;
    const today = new Date();

    if (type === "today") {
      startDate = endDate = today;
    } else if (type === "thisMonth") {
      startDate = new Date(today.getFullYear(), today.getMonth(), 1);
      endDate = today;
    } else {
      startDate = new Date(customStart);
      endDate = new Date(customEnd);
    }

    const payload = {
      start_date: format(startDate, "yyyy-MM-dd"),
      end_date: format(endDate, "yyyy-MM-dd"),
    };

    if (storeId !== "ALL") {
      payload.store_id = storeId;
    }

    axios
      .post(master.getOrderTotals, payload)
      .then((res) => {
        const data = res.data.data;

        const totalOrders = data.total_order || 0;
        const totalAmount = data.total_amount || 0;
        const totalCollected = data.total_collected || 0;
        const totalDue = data.total_due || 0;

        const orderTypeWiseData = [
          {
            order_type: "Cash",
            total_order: data.cash_order?.order || 0,
            total_amount: data.cash_order?.cash_amount || 0,
          },
          {
            order_type: "Online",
            total_order: data.online_order?.order || 0,
            total_amount: data.online_order?.online_amount || 0,
          },
        ];

        const creditData = data.credit_order
          ? {
              total_order: data.credit_order.order || 0,
              total_amount: data.credit_order.total_amount || 0,
              collected_amount: data.credit_order.collected_amount || 0,
              due_amount: data.credit_order.due_amount || 0,
            }
          : null;

        setDashboardData({
          totalOrders,
          totalAmount,
          totalCollected,
          totalDue,
          orderTypeWiseData,
          creditData,
        });
      })
      .catch((err) => console.error("Error fetching dashboard data:", err))
      .finally(() => setLoading(false));
  };

  const handleEmployeeChange = (e) => {
    const storeId = e.target.value;
    setSelectedStoreId(storeId);
    if (storeId === "") {
      setDashboardData({
        totalOrders: 0,
        totalAmount: 0,
        totalCollected: 0,
        totalDue: 0,
        orderTypeWiseData: [],
        creditData: null,
      });
      return;
    }

    if (dateFilterType === "custom") {
      fetchDashboardData(storeId, "custom", fromDate, toDate);
    } else {
      fetchDashboardData(storeId, dateFilterType);
    }
  };

  const fetchStoreList = async (adminStoreId = null, isStore = false, storeId = null) => {
    try {
      const requestData = { pageIndex: 1, pageSize: 100 };
      const res = await axios.post(master.storelist_all, requestData);
      if (res.data.flag === 1) {
        const list = res.data.data.filter(
          (store) => store.user_type === "store" || store.user_type === "admin"
        );
        setStoreList(list);

        if (list.length > 0) {
          if (isStore && storeId) {
             setSelectedStoreId(storeId);
             fetchDashboardData(storeId, dateFilterType);
          } else {
             const adminStore = list.find((s) => s.store_id === adminStoreId);
             const defaultStoreId = adminStore ? adminStore.store_id : list[0].store_id;
             setSelectedStoreId(defaultStoreId);
             fetchDashboardData(defaultStoreId, dateFilterType);
          }
        }
      }
    } catch (err) {
      console.error("Error fetching stores:", err);
    }
  };

  const resetFilters = () => {
    setFromDate("");
    setToDate("");
    setDateFilterType("today");
    if (selectedStoreId) fetchDashboardData(selectedStoreId, "today");
  };

  useEffect(() => {
    const storedUser = localStorage.getItem("user");
    let adminStoreId = null;
    let isStore = false;
    let storeId = null;

    if (storedUser) {
      const parsed = JSON.parse(storedUser);
      setLoggedInUser(parsed);
      isStore = parsed.type === "store" || parsed.user_type === "store";
      storeId = parsed.id || parsed.store_id;

      if (!isStore) {
        adminStoreId = storeId;
      }
    }
    fetchStoreList(adminStoreId, isStore, storeId);
  }, []);

  const displayDateRange = () => {
    if (dateFilterType === "today") return "Today";
    if (dateFilterType === "thisMonth") return "This Month";
    if (
      fromDate &&
      toDate &&
      dayjs(fromDate).isValid() &&
      dayjs(toDate).isValid()
    ) {
      return `${dayjs(fromDate).format("DD MMM")} - ${dayjs(toDate).format("DD MMM")}`;
    }
    return "--";
  };

  return (
    <main id="main" className="main position-relative">
      <div className="pagetitle d-flex justify-content-between align-items-center flex-wrap">
        <div>
          <h1>Dashboard</h1>
          <nav>
            <ol className="breadcrumb mb-0">
              <li className="breadcrumb-item">
                <a href="/">Home</a>
              </li>
              <li className="breadcrumb-item active">Dashboard</li>
            </ol>
          </nav>
        </div>

        <div className="d-flex flex-wrap align-items-end mb-3">
          {(loggedInUser?.type === "admin" || loggedInUser?.user_type === "admin") && (
            <div className="me-3 mb-2">
              <label
                htmlFor="employeeSelect"
                className="form-label fw-bold text-primary"
              >
                Select Store
              </label>
              <select
                id="employeeSelect"
                className="form-select shadow-sm border-primary"
                value={selectedStoreId}
                onChange={handleEmployeeChange}
              >
                <option value="ALL">All Stores</option>
                {storeList.map((store) => (
                  <option key={store.store_id} value={store.store_id}>
                    {store.store_name}
                  </option>
                ))}
              </select>
            </div>
          )}

          <div className="me-3 mb-2">
            <label className="form-label fw-bold text-primary">
              Date Filter
            </label>
            <Form.Select
              className="shadow-sm border-primary"
              value={dateFilterType}
              onChange={(e) => {
                const selectedType = e.target.value;
                setDateFilterType(selectedType);
                if (selectedType !== "custom")
                  fetchDashboardData(selectedStoreId, selectedType);
              }}
            >
              <option value="today">Today</option>
              <option value="thisMonth">This Month</option>
              <option value="custom">Custom</option>
            </Form.Select>
          </div>

          {dateFilterType === "custom" && (
            <>
              <div className="me-3 mb-2">
                <label className="form-label fw-bold text-primary">
                  From Date
                </label>
                <Form.Control
                  type="date"
                  value={fromDate}
                  onChange={(e) => setFromDate(e.target.value)}
                  max={toDate}
                  className="shadow-sm border-primary"
                />
              </div>
              <div className="me-3 mb-2">
                <label className="form-label fw-bold text-primary">
                  To Date
                </label>
                <Form.Control
                  type="date"
                  value={toDate}
                  onChange={(e) => setToDate(e.target.value)}
                  min={fromDate}
                  className="shadow-sm border-primary"
                />
              </div>
              <div className="mb-2 me-3">
                <button
                  type="button"
                  className="btn btn-primary fw-bold shadow-sm"
                  onClick={() => {
                    if (!fromDate || !toDate)
                      return alert("Please select both From and To dates.");
                    if (new Date(fromDate) > new Date(toDate))
                      return alert("From Date cannot be later than To Date.");
                    fetchDashboardData(
                      selectedStoreId,
                      "custom",
                      fromDate,
                      toDate,
                    );
                  }}
                >
                  Search
                </button>
              </div>
              <div className="mb-2 me-3">
                <button
                  type="button"
                  className="btn btn-secondary fw-bold shadow-sm"
                  onClick={resetFilters}
                >
                  Reset
                </button>
              </div>
            </>
          )}
        </div>
      </div>

      <section className="section dashboard">
        <div className="container-fluid px-0 position-relative">
          {loggedInUser?.user_type === "admin" && selectedStoreId === "" ? (
            <div className="text-center my-3">
              <img
                src="/assets/img/not-found.svg"
                alt="No Data Found"
                style={{ width: "250px", height: "auto" }}
              />
              <h5 className="text-muted mt-3">
                Please select a store to view dashboard data.
              </h5>
            </div>
          ) : (
            <div className="position-relative">
              {/* Row 1: Top Level Overviews */}
              <div className="row">
                <div className="col-xxl-3 col-lg-3 col-md-6 mb-4">
                  <div className="card info-card sales-card border-primary">
                    <div className="card-body">
                      <h5 className="card-title text-primary">
                        Total Orders <span>| {displayDateRange()}</span>
                      </h5>
                      <div className="d-flex align-items-center">
                        <div className="card-icon rounded-circle d-flex align-items-center justify-content-center bg-primary text-white">
                          <i className="bi bi-cart"></i>
                        </div>
                        <div className="ps-3">
                          <h6>{dashboardData.totalOrders}</h6>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="col-xxl-3 col-lg-3 col-md-6 mb-4">
                  <div className="card info-card revenue-card border-info">
                    <div className="card-body">
                      <h5 className="card-title text-info">
                        Gross Sale Value <span>| {displayDateRange()}</span>
                      </h5>
                      <div className="d-flex align-items-center">
                        <div className="card-icon rounded-circle d-flex align-items-center justify-content-center">
                          <i className="bi bi-receipt"></i>
                        </div>
                        <div className="ps-3">
                          <h6>₹{dashboardData.totalAmount}</h6>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="col-xxl-3 col-lg-3 col-md-6 mb-4">
                  <div className="card info-card revenue-card border-success">
                    <div className="card-body">
                      <h5 className="card-title text-success">
                        Total Collected <span>| Cash + UPI</span>
                      </h5>
                      <div className="d-flex align-items-center">
                        <div className="card-icon rounded-circle d-flex align-items-center justify-content-center bg-success text-white">
                          <i className="bi bi-wallet2"></i>
                        </div>
                        <div className="ps-3">
                          <h6>₹{dashboardData.totalCollected}</h6>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="col-xxl-3 col-lg-3 col-md-6 mb-4">
                  <div className="card info-card revenue-card border-danger">
                    <div className="card-body">
                      <h5 className="card-title text-danger">
                        Total Dues <span>| Pending</span>
                      </h5>
                      <div className="d-flex align-items-center">
                        <div className="card-icon rounded-circle d-flex align-items-center justify-content-center bg-danger text-white">
                          <i className="bi bi-exclamation-triangle"></i>
                        </div>
                        <div className="ps-3">
                          <h6>₹{dashboardData.totalDue}</h6>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Row 2: Breakdowns */}
              <div className="row">
                {/* Cash & Online Cards */}
                {dashboardData.orderTypeWiseData?.map((item, index) => (
                  <div key={index} className="col-xxl-3 col-lg-4 col-md-6 mb-4">
                    <div className="card info-card sales-card">
                      <div className="card-body">
                        <h5 className="card-title">
                          {item.order_type} Collections
                        </h5>
                        <div className="d-flex align-items-center">
                          <div className="card-icon rounded-circle d-flex align-items-center justify-content-center">
                            <i
                              className={`bi ${item.order_type === "Cash" ? "bi-cash-coin" : "bi-phone"}`}
                            ></i>
                          </div>
                          <div className="ps-3">
                            <h6>₹{item.total_amount}</h6>
                            <span className="text-muted small pt-2 ps-1">
                              From {item.total_order} Orders
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}

                {/* 🟢 NEW: Credit Detailed Card */}
                {dashboardData.creditData &&
                  dashboardData.creditData.total_order > 0 && (
                    <div className="col-xxl-6 col-lg-8 col-md-12 mb-4">
                      <div className="card info-card revenue-card border-warning">
                        <div className="card-body">
                          <h5 className="card-title text-warning">
                            Credit (Khata) Orders <span>| Breakdown</span>
                          </h5>
                          <div className="d-flex align-items-center">
                            <div
                              className="card-icon rounded-circle d-flex align-items-center justify-content-center"
                              style={{
                                backgroundColor: "#fff3cd",
                                color: "#ffc107",
                              }}
                            >
                              <i className="bi bi-journal-text"></i>
                            </div>
                            <div className="ps-4 w-100">
                              <h6 className="mb-2">
                                {dashboardData.creditData.total_order} Orders
                              </h6>
                              <div className="d-flex justify-content-between bg-light p-2 rounded border">
                                <div className="text-center">
                                  <div className="text-muted small fw-bold">
                                    Gross Value
                                  </div>
                                  <div className="fs-6 fw-bold">
                                    ₹{dashboardData.creditData.total_amount}
                                  </div>
                                </div>
                                <div className="text-center border-start border-end px-3">
                                  <div className="text-success small fw-bold">
                                    Advance Paid
                                  </div>
                                  <div className="fs-6 fw-bold text-success">
                                    ₹{dashboardData.creditData.collected_amount}
                                  </div>
                                </div>
                                <div className="text-center">
                                  <div className="text-danger small fw-bold">
                                    Pending Dues
                                  </div>
                                  <div className="fs-6 fw-bold text-danger">
                                    ₹{dashboardData.creditData.due_amount}
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
              </div>

              {/* Scoped Loading Overlay */}
              {loading && (
                <div
                  className="position-absolute top-0 start-0 w-100 h-100 d-flex justify-content-center align-items-center"
                  style={{
                    backgroundColor: "rgba(255,255,255,0.6)",
                    zIndex: 10,
                  }}
                >
                  <div
                    className="spinner-border text-primary"
                    role="status"
                    style={{ width: "3rem", height: "3rem" }}
                  >
                    <span className="visually-hidden">Loading...</span>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </section>
    </main>
  );
}

export default Dashboard;
